import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../lib/firebase';
import { recordLetsTalkRetrieval, recordLetsTalkSession } from '../lib/analytics/productEvents';

export type LetsTalkVoiceStatus = 'idle' | 'connecting' | 'connected' | 'closing' | 'error';
export interface LetsTalkCitation { documentId: string; projectId: string; fileName: string; excerpt: string; sourceNumber: number; pageNumber?: number }
export interface LetsTalkTranscript { id: string; role: 'user' | 'assistant'; text: string; startMs?: number; endMs?: number }
interface CreateSessionResponse { sessionId: string; sdp: string; expiresAt: number }
interface AnswerResponse { answer: string; citations: LetsTalkCitation[]; status: 'answered' | 'no_evidence' | 'unavailable' }
interface PendingDelegation { timer: number; deadline: number; generation: number; kind: 'initial' | 'retry'; turnId: string | null }
const MAX_CONTEXT = 4_000, MAX_TURNS = 80, ICE_TIMEOUT = 10_000, READY_TIMEOUT = 12_000, DELEGATION_GRACE = 350, INITIAL_DELEGATION_TIMEOUT = 5_000, CLOSE_TIMEOUT = 1_000;

function messageFor(error: unknown) {
  const name = (error as { name?: string })?.name;
  if (name === 'NotAllowedError') return 'Microphone permission was not granted.';
  if (name === 'NotFoundError') return 'No microphone is available.';
  const message = error instanceof Error ? error.message : '';
  if (message === 'Voice is not supported in this browser.' || message === 'Voice connection setup timed out. Please try again.' || message === 'Voice session expired before it started.') return message;
  return 'Unable to start Lets Talk. Please try again.';
}
function waitForIce(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const done = () => { clearTimeout(timer); pc.removeEventListener('icegatheringstatechange', changed); resolve(); };
    const changed = () => { if (pc.iceGatheringState === 'complete') done(); };
    const timer = window.setTimeout(() => { pc.removeEventListener('icegatheringstatechange', changed); reject(new Error('Voice connection setup timed out. Please try again.')); }, ICE_TIMEOUT);
    pc.addEventListener('icegatheringstatechange', changed);
  });
}

export function useLetsTalkVoice() {
  const { user, userProfile } = useAuth();
  const [status, setStatus] = useState<LetsTalkVoiceStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [transcripts, setTranscripts] = useState<LetsTalkTranscript[]>([]);
  const [citations, setCitations] = useState<LetsTalkCitation[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [audioPlaybackBlocked, setAudioPlaybackBlocked] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<RTCDataChannel | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const generationRef = useRef(0), startingRef = useRef(false), stoppingRef = useRef(false), startedRef = useRef(false);
  const expiresTimerRef = useRef<number | null>(null), readyTimerRef = useRef<number | null>(null);
  const playbackGateRef = useRef(false), inputRevisionRef = useRef(0);
  const transcriptRef = useRef<LetsTalkTranscript[]>([]), seenDelegationsRef = useRef(new Set<string>()), pendingDelegationsRef = useRef(new Map<string, PendingDelegation>()), delegationRetriesRef = useRef(new Map<string, number>()), latestRequestRef = useRef(0);
  const activeDelegationRef = useRef<{ id: string; request: number; turnId: string } | null>(null);
  const claimedUserTurnIdsRef = useRef(new Set<string>());
  const identityRef = useRef(`${user?.uid ?? ''}:${userProfile?.organizationId ?? ''}`);

  const closePlayback = useCallback(() => {
    playbackGateRef.current = false;
    if (audioRef.current) { audioRef.current.muted = true; void audioRef.current.play().catch(() => undefined); }
  }, []);

  const clearTimers = useCallback(() => {
    if (expiresTimerRef.current !== null) window.clearTimeout(expiresTimerRef.current);
    if (readyTimerRef.current !== null) window.clearTimeout(readyTimerRef.current);
    expiresTimerRef.current = readyTimerRef.current = null;
    for (const pending of pendingDelegationsRef.current.values()) window.clearTimeout(pending.timer);
    pendingDelegationsRef.current.clear();
    delegationRetriesRef.current.clear();
    activeDelegationRef.current = null;
  }, []);
  const retryAudio = useCallback(async () => {
    if (!audioRef.current) return;
    try { await audioRef.current.play(); setAudioPlaybackBlocked(false); setError((current) => current === 'Audio playback is blocked. Use the audio controls to continue.' ? null : current); }
    catch { setAudioPlaybackBlocked(true); setError('Audio playback is blocked. Use the audio controls to continue.'); }
  }, []);
  const openPlayback = useCallback((generation: number) => {
    if (generation !== generationRef.current) return;
    playbackGateRef.current = true;
    if (audioRef.current) { audioRef.current.muted = false; void retryAudio(); }
  }, [retryAudio]);
  const detach = useCallback((peer = peerRef.current, channel = channelRef.current, mic = micRef.current) => {
    if (peerRef.current === peer) peerRef.current = null;
    if (channelRef.current === channel) channelRef.current = null;
    if (micRef.current === mic) micRef.current = null;
    mic?.getTracks().forEach((track) => track.stop());
    if (audioRef.current) { audioRef.current.muted = true; audioRef.current.pause(); audioRef.current.srcObject = null; }
    channel?.close(); peer?.close();
  }, []);
  const closeGrant = useCallback(async (sessionId: string | null) => {
    if (!sessionId) return;
    try { await httpsCallable<{ sessionId: string }, { success: boolean }>(functions, 'closeLetsTalkSession', { timeout: 30_000 })({ sessionId }); } catch { /* expiry bounds a failed close */ }
  }, []);

  const stop = useCallback(async (unexpected = false) => {
    if (stoppingRef.current) return;
    stoppingRef.current = true; startingRef.current = false; generationRef.current += 1; clearTimers(); closePlayback();
    const sessionId = sessionIdRef.current, channel = channelRef.current, peer = peerRef.current, mic = micRef.current, wasStarted = startedRef.current;
    sessionIdRef.current = null; startedRef.current = false; setIsSearching(false); setStatus(unexpected ? 'error' : sessionId ? 'closing' : 'idle');
    const terminal = new Promise<void>((resolve) => {
      if (!channel || channel.readyState !== 'open') return resolve();
      const done = () => { window.clearTimeout(timer); channel.removeEventListener('message', listener); resolve(); };
      const listener = (event: MessageEvent) => { try { if (JSON.parse(event.data).type === 'session.closed') done(); } catch { /* ignore */ } };
      channel.addEventListener('message', listener);
      const timer = window.setTimeout(done, CLOSE_TIMEOUT);
      try { channel.send(JSON.stringify({ type: 'session.close' })); } catch { done(); }
    });
    // Never wait for a network close while the speaker or microphone remains live.
    mic?.getTracks().forEach((track) => track.stop());
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.srcObject = null; }
    await terminal;
    detach(peer, channel, mic);
    await closeGrant(sessionId);
    if (wasStarted) recordLetsTalkSession('ended');
    stoppingRef.current = false;
    if (!unexpected) setStatus('idle');
  }, [clearTimers, closeGrant, closePlayback, detach]);

  const appendDelta = useCallback((role: 'user' | 'assistant', event: { delta?: unknown; start_ms?: unknown; end_ms?: unknown }) => {
    const text = typeof event.delta === 'string' ? event.delta : ''; if (!text) return false;
    const startMs = typeof event.start_ms === 'number' ? event.start_ms : undefined, endMs = typeof event.end_ms === 'number' ? event.end_ms : undefined;
    const old = transcriptRef.current;
    // Provider acknowledgements may arrive between fragments of the same spoken
    // question. Display order must not turn that fragment into an interruption.
    const previousIndex = role === 'user' && startMs !== undefined
      ? old.map((turn) => turn.role).lastIndexOf('user') : old.length - 1;
    const previous = old[previousIndex];
    const contiguous = previous?.role === role && (previousIndex === old.length - 1
      ? startMs === undefined || previous.endMs === undefined || startMs <= previous.endMs + 750
      : previous.endMs !== undefined && startMs !== undefined && startMs <= previous.endMs + 750
        && (!claimedUserTurnIdsRef.current.has(previous.id) || activeDelegationRef.current?.turnId === previous.id
          || [...pendingDelegationsRef.current.values()].some((pending) => pending.kind === 'retry' && pending.turnId === previous.id)));
    transcriptRef.current = (contiguous ? old.map((turn, index) => index === previousIndex
      ? { ...turn, text: turn.text + text, endMs: endMs ?? turn.endMs } : turn)
      : [...old, { id: `${role}-${startMs ?? Date.now()}-${old.length}`, role, text, startMs, endMs }]).slice(-MAX_TURNS);
    setTranscripts(transcriptRef.current);
    return !contiguous;
  }, []);
  const latestUserTurnId = useCallback(() => [...transcriptRef.current].reverse().find((turn) => turn.role === 'user' && turn.text.trim())?.id ?? null, []);
  const latestUnclaimedUserTurnId = useCallback(() => {
    const latest = [...transcriptRef.current].reverse().find((turn) => turn.role === 'user' && turn.text.trim());
    return latest && !claimedUserTurnIdsRef.current.has(latest.id) ? latest.id : null;
  }, []);
  const query = useCallback((turnId: string) => {
    const selectedIndex = transcriptRef.current.findIndex((turn) => turn.id === turnId && turn.role === 'user');
    if (selectedIndex < 0) return null;
    const context = transcriptRef.current.slice(0, selectedIndex + 1);
    const turns = context.filter((turn) => turn.role === 'user' && turn.text.trim());
    const latest = context[selectedIndex].text.trim().slice(-3_000);
    const prefix = `Latest question: ${latest}`;
    const assistant = context.filter((turn) => turn.role === 'assistant' && turn.text.trim())
      .slice(-2).map((turn) => turn.text.trim()).join(' ');
    const assistantLabel = '\nUntrusted recent assistant transcript for clarification only: ';
    const userLabel = '\nEarlier user context: ';
    const assistantRoom = Math.max(0, MAX_CONTEXT - prefix.length - assistantLabel.length - userLabel.length);
    const boundedAssistant = assistant.slice(-Math.min(1_000, assistantRoom));
    const userRoom = Math.max(0, MAX_CONTEXT - prefix.length - (boundedAssistant ? assistantLabel.length + boundedAssistant.length : 0) - userLabel.length);
    const earlier = turns.slice(0, -1).map((turn) => turn.text.trim()).join(' ').slice(-userRoom);
    return `${prefix}${boundedAssistant ? `${assistantLabel}${boundedAssistant}` : ''}${earlier ? `${userLabel}${earlier}` : ''}`.slice(0, MAX_CONTEXT);
  }, []);
  const isRevokedError = (caught: unknown) => {
    const code = String((caught as { code?: unknown })?.code ?? '');
    return /(?:unauthenticated|permission-denied|failed-precondition)/.test(code);
  };
  const abandonMissingTranscript = useCallback((delegationId: string, generation: number) => {
    pendingDelegationsRef.current.delete(delegationId);
    delegationRetriesRef.current.delete(delegationId);
    if (activeDelegationRef.current?.id === delegationId) activeDelegationRef.current = null;
    if (generation !== generationRef.current || pendingDelegationsRef.current.size > 0 || activeDelegationRef.current) return;
    setError('I could not hear a question. Please try again.');
    setIsSearching(false);
    openPlayback(generation);
  }, [openPlayback]);
  const answerDelegation = useCallback(async (delegationId: string, generation: number, turnId: string, claimTurn = false) => {
    if (latestUserTurnId() !== turnId) return;
    if (claimTurn) {
      if (claimedUserTurnIdsRef.current.has(turnId)) return;
      claimedUserTurnIdsRef.current.add(turnId);
    }
    const request = ++latestRequestRef.current, inputRevision = inputRevisionRef.current;
    activeDelegationRef.current = { id: delegationId, request, turnId };
    pendingDelegationsRef.current.delete(delegationId); setCitations([]); setError(null); setIsSearching(true); closePlayback();
    const scheduleRevisionRetry = () => {
      if (latestUserTurnId() !== turnId) return false;
      const retries = (delegationRetriesRef.current.get(delegationId) ?? 0) + 1;
      delegationRetriesRef.current.set(delegationId, retries);
      if (retries > 2) return false;
      const deadline = Date.now() + DELEGATION_GRACE * 2;
      const timer = window.setTimeout(() => {
        const pending = pendingDelegationsRef.current.get(delegationId);
        if (pending?.timer !== timer) return;
        pendingDelegationsRef.current.delete(delegationId);
        if (generation === generationRef.current && latestUserTurnId() === turnId) void answerDelegation(delegationId, generation, turnId);
      }, DELEGATION_GRACE);
      pendingDelegationsRef.current.set(delegationId, { timer, deadline, generation, kind: 'retry', turnId });
      return true;
    };
    const relay = (content: string) => {
      const channel = channelRef.current;
      if (channel?.readyState !== 'open') throw new Error('Voice response channel is unavailable.');
      channel.send(JSON.stringify({ type: 'session.commentary.append', delegation_id: delegationId, content: content.slice(0, 2_000) }));
      openPlayback(generation);
    };
    try {
      const ownedQuery = query(turnId);
      if (!ownedQuery) return;
      const result = await httpsCallable<{ sessionId: string; query: string }, AnswerResponse>(functions, 'answerLetsTalk', { timeout: 120_000 })({ sessionId: sessionIdRef.current ?? '', query: ownedQuery });
      if (generation !== generationRef.current || request !== latestRequestRef.current || !sessionIdRef.current) return;
      if (inputRevision !== inputRevisionRef.current && latestUserTurnId() === turnId) {
        if (scheduleRevisionRetry()) return;
        setError('Your question changed while I was searching. Please try again.');
        relay('Your question changed while I was searching. Please try again.');
        return;
      }
      delegationRetriesRef.current.delete(delegationId);
      setCitations(result.data.citations); recordLetsTalkRetrieval(result.data.status, result.data.citations.length);
      // The server distinguishes unverified evidence from an unavailable search.
      // Keep that distinction in the spoken response instead of replacing it here.
      const content = result.data.answer;
      relay(content);
    } catch (caught) {
      if (generation !== generationRef.current || request !== latestRequestRef.current) return;
      if (isRevokedError(caught)) { setError('Your Lets Talk access changed. Please restart.'); void stop(true); return; }
      if (inputRevision !== inputRevisionRef.current && latestUserTurnId() === turnId) {
        if (scheduleRevisionRetry()) return;
        setError('Your question changed while I was searching. Please try again.');
        try { relay('Your question changed while I was searching. Please try again.'); } catch { openPlayback(generation); }
        return;
      }
      delegationRetriesRef.current.delete(delegationId);
      recordLetsTalkRetrieval('unavailable', 0); setError('Document search is unavailable. Please try again.');
      try { relay('I cannot access the available documents right now. Please try again.'); }
      catch { setError('Voice response could not be delivered. Please try again.'); openPlayback(generation); }
    } finally {
      if (generation === generationRef.current && request === latestRequestRef.current && !pendingDelegationsRef.current.has(delegationId)) {
        activeDelegationRef.current = null;
        setIsSearching(false);
        if (!playbackGateRef.current) openPlayback(generation);
      }
    }
  }, [closePlayback, latestUserTurnId, openPlayback, query, stop]);
  const scheduleInitialDelegation = useCallback((id: string, generation: number, deadline: number, expectedTurnId: string | null) => {
    const schedule = (turnId: string | null) => {
      const delay = Math.max(0, Math.min(DELEGATION_GRACE, deadline - Date.now()));
      const timer = window.setTimeout(() => {
        const pending = pendingDelegationsRef.current.get(id);
        if (pending?.timer !== timer) return;
        const candidate = latestUnclaimedUserTurnId();
        if (candidate && candidate === pending.turnId && generation === generationRef.current) {
          void answerDelegation(id, generation, candidate, true);
          return;
        }
        if (Date.now() >= deadline || generation !== generationRef.current) {
          abandonMissingTranscript(id, generation);
          return;
        }
        schedule(candidate);
      }, delay);
      pendingDelegationsRef.current.set(id, { timer, deadline, generation, kind: 'initial', turnId });
    };
    schedule(expectedTurnId);
  }, [abandonMissingTranscript, answerDelegation, latestUnclaimedUserTurnId]);
  const queueDelegation = useCallback((id: string, generation: number) => {
    if (seenDelegationsRef.current.has(id)) return;
    seenDelegationsRef.current.add(id);
    // A new provider delegation owns the next grounded response. Invalidate an older
    // in-flight request immediately so it cannot speak or unmute while this one settles.
    latestRequestRef.current += 1;
    activeDelegationRef.current = null;
    for (const [pendingId, pending] of pendingDelegationsRef.current) {
      window.clearTimeout(pending.timer);
      pendingDelegationsRef.current.delete(pendingId);
      delegationRetriesRef.current.delete(pendingId);
    }
    closePlayback();
    setIsSearching(true);
    const deadline = Date.now() + INITIAL_DELEGATION_TIMEOUT;
    scheduleInitialDelegation(id, generation, deadline, latestUnclaimedUserTurnId());
  }, [closePlayback, latestUnclaimedUserTurnId, scheduleInitialDelegation]);

  const start = useCallback(async () => {
    if (!user || startingRef.current || stoppingRef.current || peerRef.current) { if (!user) setError('Please sign in to use Lets Talk.'); return; }
    startingRef.current = true;
    const generation = ++generationRef.current;
    let peer: RTCPeerConnection | null = null, channel: RTCDataChannel | null = null, mic: MediaStream | null = null, sessionId: string | null = null;
    window.dispatchEvent(new CustomEvent('returncatalyst:voice-start', { detail: { source: 'lets-talk' } }));
    closePlayback(); setError(null); setAudioPlaybackBlocked(false); setIsMuted(false); setCitations([]); setIsSearching(false); transcriptRef.current = []; setTranscripts([]); seenDelegationsRef.current.clear(); delegationRetriesRef.current.clear(); claimedUserTurnIdsRef.current.clear(); latestRequestRef.current = 0; inputRevisionRef.current = 0; setStatus('connecting');
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === 'undefined') throw new Error('Voice is not supported in this browser.');
      mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (generation !== generationRef.current) { mic.getTracks().forEach((track) => track.stop()); return; }
      micRef.current = mic; peer = new RTCPeerConnection(); peerRef.current = peer;
      peer.onconnectionstatechange = () => { if (generation === generationRef.current && (peer?.connectionState === 'failed' || peer?.connectionState === 'disconnected') && !stoppingRef.current) { setError('Voice connection ended unexpectedly. Please restart.'); void stop(true); } };
      peer.ontrack = (event) => { if (generation === generationRef.current && audioRef.current) { audioRef.current.srcObject = event.streams[0] ?? new MediaStream([event.track]); audioRef.current.muted = !playbackGateRef.current; if (playbackGateRef.current) void retryAudio(); } };
      mic.getTracks().forEach((track) => peer?.addTrack(track, mic as MediaStream));
      channel = peer.createDataChannel('oai-events'); channelRef.current = channel;
      channel.addEventListener('close', () => { if (generation === generationRef.current && !stoppingRef.current) { setError('Voice connection ended unexpectedly. Please restart.'); void stop(true); } });
      channel.addEventListener('error', () => { if (generation === generationRef.current && !stoppingRef.current) { setError('Voice connection encountered an error. Please restart.'); void stop(true); } });
      channel.addEventListener('message', (message) => {
        if (generation !== generationRef.current) return;
        try {
          const event = JSON.parse(message.data) as { type?: string; delta?: unknown; start_ms?: unknown; end_ms?: unknown; delegation?: { id?: unknown; type?: unknown; target?: unknown } };
          if (event.type === 'session.started') { if (readyTimerRef.current !== null) window.clearTimeout(readyTimerRef.current); readyTimerRef.current = null; setStatus('connected'); openPlayback(generation); if (!startedRef.current) { startedRef.current = true; recordLetsTalkSession('started'); } }
          else if (event.type === 'session.closed') void stop();
          else if (event.type === 'error') { closePlayback(); setError('Voice session encountered an error. Please restart.'); void stop(true); }
          else if (event.type === 'session.input_transcript.delta') {
            if (typeof event.delta !== 'string' || !event.delta) return;
            inputRevisionRef.current += 1;
            const newSegment = appendDelta('user', event), currentTurnId = latestUserTurnId();
            const activeDelegation = activeDelegationRef.current;
            if (activeDelegation) {
              // Supersede the partial request now. Waiting for its network response
              // before searching the completed question adds an entire search delay.
              latestRequestRef.current += 1;
              activeDelegationRef.current = null;
              if (!newSegment && activeDelegation.turnId === currentTurnId) {
                const retries = (delegationRetriesRef.current.get(activeDelegation.id) ?? 0) + 1;
                delegationRetriesRef.current.set(activeDelegation.id, retries);
                if (retries <= 2) {
                  const deadline = Date.now() + DELEGATION_GRACE * 2;
                  const timer = window.setTimeout(() => {
                    const pending = pendingDelegationsRef.current.get(activeDelegation.id);
                    if (pending?.timer !== timer) return;
                    pendingDelegationsRef.current.delete(activeDelegation.id);
                    if (generation === generationRef.current && latestUserTurnId() === activeDelegation.turnId) void answerDelegation(activeDelegation.id, generation, activeDelegation.turnId);
                  }, DELEGATION_GRACE);
                  pendingDelegationsRef.current.set(activeDelegation.id, { timer, deadline, generation, kind: 'retry', turnId: activeDelegation.turnId });
                } else {
                  const clarification = 'Your question changed while I was searching. Please try again.';
                  setError(clarification);
                  setIsSearching(false);
                  try {
                    if (channelRef.current?.readyState === 'open') channelRef.current.send(JSON.stringify({ type: 'session.commentary.append', delegation_id: activeDelegation.id, content: clarification }));
                  } catch { /* the visible clarification remains available */ }
                  openPlayback(generation);
                }
              }
            }
            for (const [id, pending] of [...pendingDelegationsRef.current]) {
              window.clearTimeout(pending.timer);
              if (pending.kind === 'initial') {
                scheduleInitialDelegation(id, generation, pending.deadline, latestUnclaimedUserTurnId());
                continue;
              }
              if (newSegment && pending.kind === 'retry' && pending.turnId !== currentTurnId) {
                pendingDelegationsRef.current.delete(id);
                delegationRetriesRef.current.delete(id);
                continue;
              }
              const turnId = pending.turnId;
              const delay = Math.max(0, Math.min(DELEGATION_GRACE, pending.deadline - Date.now()));
              const timer = window.setTimeout(() => {
                const latestPending = pendingDelegationsRef.current.get(id);
                if (latestPending?.timer !== timer) return;
                pendingDelegationsRef.current.delete(id);
                const ownedTurnId = latestPending.turnId;
                if (ownedTurnId && generation === generationRef.current && latestUserTurnId() === ownedTurnId) void answerDelegation(id, generation, ownedTurnId);
                else abandonMissingTranscript(id, generation);
              }, delay);
              pendingDelegationsRef.current.set(id, { ...pending, timer, turnId });
            }
            if (newSegment && !activeDelegationRef.current && pendingDelegationsRef.current.size === 0) {
              setIsSearching(false);
              openPlayback(generation);
            }
          }
          else if (event.type === 'session.output_transcript.delta') appendDelta('assistant', event);
          else if (event.type === 'session.delegation.created' && event.delegation?.type === 'delegation' && event.delegation.target === 'client' && typeof event.delegation.id === 'string') queueDelegation(event.delegation.id, generation);
        } catch { /* malformed provider events have no user-visible detail */ }
      });
      const offer = await peer.createOffer(); await peer.setLocalDescription(offer); await waitForIce(peer);
      if (generation !== generationRef.current || !peer.localDescription?.sdp) return;
      const created = await httpsCallable<{ sdp: string }, CreateSessionResponse>(functions, 'createLetsTalkSession')({ sdp: peer.localDescription.sdp });
      sessionId = created.data.sessionId;
      if (generation !== generationRef.current) { await closeGrant(sessionId); return; }
      sessionIdRef.current = sessionId; await peer.setRemoteDescription({ type: 'answer', sdp: created.data.sdp });
      if (generation !== generationRef.current) { await closeGrant(sessionId); return; }
      const remaining = created.data.expiresAt - Date.now(); if (remaining <= 0) throw new Error('Voice session expired before it started.');
      expiresTimerRef.current = window.setTimeout(() => { void stop(); }, remaining);
      readyTimerRef.current = window.setTimeout(() => { if (generation === generationRef.current && !startedRef.current) { setError('Voice session did not become ready. Please try again.'); void stop(true); } }, READY_TIMEOUT);
    } catch (caught) {
      if (generation === generationRef.current) { closePlayback(); setError(messageFor(caught)); setStatus('error'); recordLetsTalkSession('failed'); sessionIdRef.current = null; clearTimers(); detach(peer, channel, mic); }
      if (sessionId) await closeGrant(sessionId);
    } finally { if (generation === generationRef.current) startingRef.current = false; }
  }, [abandonMissingTranscript, answerDelegation, appendDelta, clearTimers, closeGrant, closePlayback, detach, latestUnclaimedUserTurnId, latestUserTurnId, openPlayback, queueDelegation, retryAudio, scheduleInitialDelegation, stop, user]);
  const toggleMute = useCallback(() => { const next = !isMuted; micRef.current?.getAudioTracks().forEach((track) => { track.enabled = !next; }); setIsMuted(next); }, [isMuted]);

  useEffect(() => { const pageHide = () => { void stop(); }; window.addEventListener('pagehide', pageHide); return () => { window.removeEventListener('pagehide', pageHide); void stop(); }; }, [stop]);
  useEffect(() => { const identity = `${user?.uid ?? ''}:${userProfile?.organizationId ?? ''}`; if (identityRef.current !== identity) { identityRef.current = identity; transcriptRef.current = []; setTranscripts([]); setCitations([]); void stop(); } }, [stop, user?.uid, userProfile?.organizationId]);
  useEffect(() => { const otherVoice = (event: Event) => { if ((event as CustomEvent<{ source?: string }>).detail?.source !== 'lets-talk' && (peerRef.current || startingRef.current)) void stop(); }; window.addEventListener('returncatalyst:voice-start', otherVoice); return () => window.removeEventListener('returncatalyst:voice-start', otherVoice); }, [stop]);
  return { status, error, isMuted, transcripts, citations, isSearching, audioPlaybackBlocked, start, stop, toggleMute, audioRef, retryAudio, resumeAudio: retryAudio };
}
